import * as React from 'react';

import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import {QuoteAndInvoiceInfoModal} from 'shells/modal/quote-and-invoice-info-modal';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';
import type {
    GetQuotes_quotes as Quote,
    GetInvoices_invoices as Invoice,
} from 'nutshell-graphql-types';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {EmailModalWrapper} from '../../../core-entity/components/email-modal-wrapper';

import {refetchForEvent} from '../../refetch-helpers';
import {useCreateInvoiceFromQuote} from '../../sales-documents/quotes/graphql/hooks';
import {
    downloadQuote,
    preventRefetchLoadingState,
    handleSalesDocumentChange,
} from '../../sales-documents/shared';
import {
    useUpdateSalesDocumentStatus,
    useDuplicateSalesDocument,
} from '../../sales-documents/shared/graphql/hooks';

import {SalesDocumentsRowList} from './sales-documents-row-list';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quotes-container';

type Props = {
    leadId: string;
    refetchLoadingRef: {
        current: boolean;
    };
    quotes: Quote[];
    invoices: Invoice[];
    refetchQuotes: () => Promise<any>;
    refetchInvoices: () => Promise<any>;
};

export function SidebarSalesDocumentsSection(props: Props) {
    const [isInfoModalOpen, setIsInfoModalOpen] = React.useState<boolean>(false);
    const [emailModalType, setEmailModalType] = React.useState<SalesDocumentType | null>(null);

    const {sessionInstance} = useGetInstanceInfo();
    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);

    const {updateSalesDocumentStatus} = useUpdateSalesDocumentStatus();
    const {duplicateSalesDocument} = useDuplicateSalesDocument();
    const {createInvoiceFromQuote} = useCreateInvoiceFromQuote();

    const instanceId = sessionInstance && sessionInstance.instanceId;

    const handleSendQuoteViaEmail = React.useCallback(() => {
        setEmailModalType('Quote');
    }, []);

    const handleSendInvoiceViaEmail = React.useCallback(() => {
        setEmailModalType('Invoice');
    }, []);

    const handlePostEmailModalOpen = () => {
        setEmailModalType(null);
    };

    const handleDownload = (slug: string) => {
        if (!instanceId || !scriptRef.current) {
            return;
        }

        downloadQuote(instanceId, slug, scriptRef.current, NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID);
    };

    const handleUpdateStatus = (id: string, status: QuoteDisplayStatus) => {
        updateSalesDocumentStatus({
            id: id,
            status,
        }).then((response) => {
            if (NutClientConfig.hasLeadPageRedesignM3FF) {
                refetchForEvent('salesDocumentStatusChange');
            } else {
                Timeline.refreshTimeline();
            }

            const salesDocumentRes =
                response && response.data && response.data.salesDocumentUpdateStatus;

            if (salesDocumentRes && salesDocumentRes.salesDocument) {
                preventRefetchLoadingState(
                    props.refetchLoadingRef,
                    salesDocumentRes.salesDocument.__typename === 'Quote'
                        ? props.refetchQuotes
                        : props.refetchInvoices
                );
            }
        });
    };

    const handleDuplicateSalesDocument = (id: string) => {
        duplicateSalesDocument({
            salesDocument: id,
        }).then((response) => {
            const salesDocumentRes =
                response && response.data && response.data.salesDocumentDuplicate;
            if (salesDocumentRes && salesDocumentRes.salesDocument) {
                handleSalesDocumentChange(
                    salesDocumentRes.salesDocument.modalUrl,
                    props.refetchLoadingRef,
                    salesDocumentRes.salesDocument.__typename === 'Quote'
                        ? props.refetchQuotes
                        : props.refetchInvoices
                );
            }
        });
    };

    const handleCreateInvoiceFromQuote = (id: string) => {
        createInvoiceFromQuote({
            quote: id,
        }).then((response) => {
            const invoiceRes = response && response.data && response.data.invoiceCreateFromQuote;
            if (invoiceRes && invoiceRes.invoice) {
                handleSalesDocumentChange(
                    invoiceRes.invoice.modalUrl,
                    props.refetchLoadingRef,
                    props.refetchInvoices
                );
            }
        });
    };

    return (
        <>
            <SalesDocumentsRowList
                quoteProps={{
                    quotes: props.quotes,
                    handleDownloadQuote: handleDownload,
                    handleUpdateQuoteStatus: handleUpdateStatus,
                    handleDuplicateQuote: handleDuplicateSalesDocument,
                    handleSendQuoteViaEmail: handleSendQuoteViaEmail,
                    handleCreateInvoiceFromQuote: handleCreateInvoiceFromQuote,
                }}
                invoiceProps={{
                    invoices: props.invoices,
                    handleDownloadInvoice: handleDownload,
                    handleUpdateInvoiceStatus: handleUpdateStatus,
                    handleDuplicateInvoice: handleDuplicateSalesDocument,
                    handleSendInvoiceViaEmail: handleSendInvoiceViaEmail,
                }}
            />
            <QuoteAndInvoiceInfoModal
                isOpen={isInfoModalOpen}
                onClose={() => setIsInfoModalOpen(false)}
            />
            {emailModalType && props.leadId && (
                <EmailModalWrapper
                    entityId={props.leadId}
                    postModalOpen={handlePostEmailModalOpen}
                    originSalesDocumentType={emailModalType}
                />
            )}
            {/* @ts-expect-error */}
            <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
        </>
    );
}
